
import { formatNumber } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { ActionsDialogItem, DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { LocaleService } from '@next/core-lib/i18n';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED, RouterNavigatedAction } from '@ngrx/router-store';
import { select, Store } from '@ngrx/store';
import { EMPTY, forkJoin, from, of, ReplaySubject, Subject } from 'rxjs';
import {
  catchError,
  delay,
  filter,
  finalize,
  map,
  mergeMap,
  startWith,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  SetExecutedTreatments,
  SetRecommendedNutrientQuantityByAdviceIds,
  SetSelectedCropFieldIds,
} from 'src/app/advice/store/actions';
import { TreatmentType } from 'src/app/sales-order-period/models/sales-order-period.models';
import { selectPastSalesOrderPeriods } from 'src/app/sales-order-period/store/sales-order-period.selectors';
import { CropData, CropFieldAdvice, ReportType, SiteState } from 'src/app/shared/models';
import { ApiService } from 'src/app/shared/services/api.service';
import { ArableAdviceService } from 'src/app/shared/services/arable-advice.service'
import { TranslationHelpers } from 'src/app/shared/util/translationHelpers';
import { CropFieldDeleted, GetCropFieldAdvices } from 'src/app/store/actions';
import {
  getCropFieldIds,
  getCropYear,
  selectAvailableCropFieldAdvices,
  selectCrops,
} from 'src/app/store/selectors';
import { Environment } from 'src/environments/environment';

import {
  CopyDefaultCropCareAdvice,
  CopyDefaultAdviceSuccess,
  DeleteMultipleCropFields,
  OpenDefaultReport,
  OpenEnvironmentalImpactReport,
  OpenFertilizationSplitFields,
  OpenFertilizationTableReport,
  OpenLegalStandards,
  OpenNutrientBalanceReport,
  OpenOrganicMatterReport,
  OpenPlanetProofReport,
  OpenSkalReport,
  OverviewActionTypes, CopyDefaultFertilizationAdvice,
} from './actions';
import {
  SelectCropFieldsDialogComponent,
  SelectCropFieldsDialogData,
  SelectCropFieldsDialogItem,
} from '../../shared/components/select-cropfields-dialog/select-cropfields-dialog.component';

@Injectable()
export class OverviewEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly dialog = inject(DialogService);
  private readonly router = inject(Router);
  private readonly fieldPlanService = inject(ArableAdviceService);
  private readonly translationHelpers = inject(TranslationHelpers);
  protected readonly api = inject(ApiService);
  private readonly env = inject<Environment>(EnvironmentInjector);
  private readonly appInsights = inject<AppInsightsService>(AppInsightsServiceInjector);

  destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  navigated$ = new Subject<void>();
  dialogOptionPlanetProof: ActionsDialogItem<ReportType> = {
    value: ReportType.PlanetProof,
    name: 'gbmPlanetProofReport',
  };

  dialogOptions = (): ActionsDialogItem<ReportType>[] => {
    const dialogOptions = [
      {
        value: ReportType.Fertilization,
        name: 'createFertilizationAdviceReport',
      },
      {
        value: ReportType.FertilizationSplitFields,
        name: 'createFertilizationAdviceReportSplitFields',
      },
      {
        value: ReportType.FertilizationTableReport,
        name: 'fertilizationTableReport',
      },
      {
        value: ReportType.LegalStandards,
        name: 'fertilizationLegalStandardsReportTitle',
      },
      {
        value: ReportType.Skal,
        name: 'skalReport',
      },
      {
        value: ReportType.OrganicMatter,
        name: 'organicMatterBalanceReport',
      },
      {
        value: ReportType.NutrientBalance,
        name: 'nutrientBalanceReport',
      },
      {
        value: ReportType.EnvironmentalImpact,
        name: 'environmentalImpactReport',
      },
    ];

    if (this.env.SHOW_GMB_WITH_EIP_REPORT) {
      dialogOptions.splice(0, 0, {
        value: ReportType.GBMWithEnvironmentalImpactPoints,
        name: 'createGBMAdviceReportWithEIP',
      });
    }
    return dialogOptions;
  };

  copyDefaultCropCareAdvices$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CopyDefaultCropCareAdvice>(OverviewActionTypes.CopyDefaultCropCareAdvice),
      withLatestFrom(this.store.pipe(select(getCropYear))),
      switchMap(([action, cropYear]) =>
        this.api.copyDefaultCropCareAdvices(action.customerId, cropYear).pipe(
          map(
            (response) =>
              new CopyDefaultAdviceSuccess(
                response,
              ),
          ),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  CopyDefaultFertilizationAdvices$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CopyDefaultFertilizationAdvice>(OverviewActionTypes.CopyDefaultFertilizationAdvice),
      withLatestFrom(this.store.pipe(select(getCropYear))),
      switchMap(([action, cropYear]) =>
        this.api.copyDefaultFertilizationAdvices(action.customerId, cropYear).pipe(
          map(
            (response) =>
              new CopyDefaultAdviceSuccess(
                response,
              ),
          ),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  copyDefaultAdvicesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CopyDefaultAdviceSuccess>(OverviewActionTypes.CopyDefaultAdvicesSuccess),
      tap((action: CopyDefaultAdviceSuccess) => {
        if (action.advices?.noDefaultAdviceFound.length > 0) {

          const noDefaultCropCareAdviceFoundList = action.advices.noDefaultAdviceFound
            .map((field) => `<li>${field.name}</li>`)
            .join('');

          const noDefaultAdviceFoundHtml = `<ul>${noDefaultCropCareAdviceFoundList}</ul>`;

          this.dialog.confirm({
            title: this.locale.translate('copyDefaultAdvice.modal.copyResultTitle'),
            text: this.locale.translate(
              'copyDefaultAdvice.modal.fieldsNoDefaultAdviceFoundDescription',
              { noDefaultAdviceFoundFields: noDefaultAdviceFoundHtml },
            ),
            pushHistoryState: false,
            cancelButton: false,
          });
        }
      }),
      map(() => new GetCropFieldAdvices()),
    ),
  );

  createAdviceReport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OverviewActionTypes.CreateAdviceReport),
        withLatestFrom(
          this.store.pipe(
            select(AuthorizationSelectors.hasAtLeastAdvisorRoleForApplicationKey('PlanetProof')),
          ),
        ),
        switchMap(([_, hasPlanetProofAuthorization]) =>
          this.dialog
            .actions({
              options: hasPlanetProofAuthorization
                ? [
                  ...this.getOptionsWithTranslations(this.dialogOptions()),
                  this.getOptionsWithTranslations([this.dialogOptionPlanetProof])[0],
                ]
                : this.getOptionsWithTranslations(this.dialogOptions()),
            })
            .pipe(
              filter(({ status }) => status === DIALOG_RETURN_TYPES.confirm),
              switchMap(({ result }) => {
                if (result === ReportType.LegalStandards) {
                  this.appInsights.trackEvent(
                    `FieldAdviceOpenReports.${ReportType.LegalStandards}`,
                    {},
                  );
                  return of(this.store.dispatch(new OpenLegalStandards()));
                }

                if (result === ReportType.Skal) {
                  this.appInsights.trackEvent(`FieldAdviceOpenReports.${ReportType.Skal}`, {});
                  return of(this.store.dispatch(new OpenSkalReport()));
                }

                if (result === ReportType.FertilizationTableReport) {
                  this.appInsights.trackEvent(
                    `FieldAdviceOpenReports.${ReportType.FertilizationTableReport}`,
                    {},
                  );
                  return of(this.store.dispatch(new OpenFertilizationTableReport()));
                }

                if (result === ReportType.FertilizationSplitFields) {
                  this.appInsights.trackEvent(
                    `FieldAdviceOpenReports.${ReportType.FertilizationSplitFields}`,
                    {},
                  );
                  return of(this.store.dispatch(new OpenFertilizationSplitFields()));
                }

                if (result === ReportType.OrganicMatter) {
                  this.appInsights.trackEvent(
                    `FieldAdviceOpenReports.${ReportType.OrganicMatter}`,
                    {},
                  );
                  return of(this.store.dispatch(new OpenOrganicMatterReport()));
                }

                if (result === ReportType.NutrientBalance) {
                  this.appInsights.trackEvent(
                    `FieldAdviceOpenReports.${ReportType.NutrientBalance}`,
                    {},
                  );
                  return of(this.store.dispatch(new OpenNutrientBalanceReport()));
                }

                if (result === ReportType.EnvironmentalImpact) {
                  this.appInsights.trackEvent(
                    `FieldAdviceOpenReports.${ReportType.EnvironmentalImpact}`,
                    {},
                  );
                  return of(this.store.dispatch(new OpenEnvironmentalImpactReport()));
                }

                if (result === ReportType.PlanetProof) {
                  this.appInsights.trackEvent(
                    `FieldAdviceOpenReports.${ReportType.PlanetProof}`,
                    {},
                  );
                  return of(this.store.dispatch(new OpenPlanetProofReport()));
                }

                if (result === ReportType.GBMWithEnvironmentalImpactPoints) {
                  this.appInsights.trackEvent(
                    `FieldAdviceOpenReports.${ReportType.GBMWithEnvironmentalImpactPoints}`,
                    {},
                  );
                  return of(
                    this.store.dispatch(
                      new OpenDefaultReport(ReportType.GBMWithEnvironmentalImpactPoints),
                    ),
                  );
                }

                if (result === ReportType.Fertilization) {
                  this.appInsights.trackEvent(
                    `FieldAdviceOpenReports.${ReportType.Fertilization}`,
                    {},
                  );
                }
                return of(this.store.dispatch(new OpenDefaultReport(result)));
              }),
            ),
        ),
      ),
    { dispatch: false },
  );

  openDefaultReport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OverviewActionTypes.OpenDefaultReport),
        mergeMap(({ reportType }: OpenDefaultReport) =>
          of(reportType).pipe(
            withLatestFrom(
              this.store.pipe(
                takeUntil(this.navigated$),
                select(
                  selectAvailableCropFieldAdvices({
                    treatmentType:
                      reportType === ReportType.GBMWithEnvironmentalImpactPoints
                        ? TreatmentType.GBM
                        : TreatmentType.Fertilization,
                  }),
                ),
              ),
              this.store.pipe(select(selectCrops)),
            ),
          ),
        ),
        delay(100),
        switchMap(([reportType, cropFieldAdvices, crops]) => {
          const selectCropFieldsDialogData = this.getFieldSelectDialogData(cropFieldAdvices, crops);
          return this.dialog.form(SelectCropFieldsDialogComponent, selectCropFieldsDialogData).pipe(
            delay(100),
            map(({ status, result }) => {
              if (status === DIALOG_RETURN_TYPES.confirm && result.length > 0) {
                const cropFieldIds = result.map((cropFieldAdvice) => cropFieldAdvice.cropFieldId);
                this.navigated$.next();
                this.router.navigate(['report', JSON.stringify(cropFieldIds), reportType, '']);
              }
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  openFertilizationSplitFields$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OverviewActionTypes.OpenFertilizationSplitFields),
        mergeMap(({ reportType }: OpenDefaultReport) =>
          of(reportType).pipe(
            withLatestFrom(
              this.store.pipe(
                takeUntil(this.navigated$),
                select(
                  selectAvailableCropFieldAdvices({
                    treatmentType: TreatmentType.Fertilization,
                  }),
                ),
              ),
              this.store.pipe(select(selectCrops)),
            ),
          ),
        ),
        delay(100),
        switchMap(([_, cropFieldAdvices, crops]) => {
          const selectCropFieldsDialogData = this.getFieldSelectDialogData(cropFieldAdvices, crops);
          return this.dialog.form(SelectCropFieldsDialogComponent, selectCropFieldsDialogData).pipe(
            delay(100),
            map(({ status, result }) => {
              if (status === DIALOG_RETURN_TYPES.confirm && result.length > 0) {
                const cropFieldIds = result.map((cropFieldAdvice) => cropFieldAdvice.cropFieldId);
                this.navigated$.next();
                this.router.navigate([
                  'report',
                  JSON.stringify(cropFieldIds),
                  ReportType.Fertilization,
                  'split',
                ]);
              }
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  openFertilizationTableReport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OverviewActionTypes.OpenFertilizationTableReport),
        mergeMap(({ reportType }: OpenDefaultReport) =>
          of(reportType).pipe(
            withLatestFrom(
              this.store.pipe(
                takeUntil(this.navigated$),
                select(
                  selectAvailableCropFieldAdvices({
                    treatmentType: TreatmentType.Fertilization,
                  }),
                ),
              ),
              this.store.pipe(select(selectCrops)),
            ),
          ),
        ),
        delay(100),
        switchMap(([_, cropFieldAdvices, crops]) => {
          const selectCropFieldsDialogData = this.getFieldSelectDialogData(cropFieldAdvices, crops);
          return this.dialog.form(SelectCropFieldsDialogComponent, selectCropFieldsDialogData).pipe(
            delay(100),
            withLatestFrom(this.store.pipe(select(getCropYear))),
            map(([{ status, result }, cropYear]) => {
              if (status === DIALOG_RETURN_TYPES.confirm && !!result?.length) {
                const cropFieldIds = result.map((cropFieldAdvice) => cropFieldAdvice.cropFieldId);
                const adviceIds = cropFieldAdvices
                  .filter((cropFieldAdvice) => cropFieldIds.includes(cropFieldAdvice.cropFieldId))
                  .map((cropFieldAdvice) => cropFieldAdvice.adviceId);

                return forkJoin([
                  this.api.getExecutedCropFieldTreatmentsByAdviceIds(adviceIds),
                  this.api.getCropFieldNutrientRequisitesByAdviceIds(cropFieldIds, cropYear),
                ])
                  .pipe(
                    withLatestFrom(this.store.pipe(select(selectPastSalesOrderPeriods))),
                    map(([responses, pastSalesOrderPeriods]) => {
                      this.store.dispatch(
                        new SetExecutedTreatments(
                          responses[0].executedTreatments,
                          pastSalesOrderPeriods,
                        ),
                      );
                      this.store.dispatch(
                        new SetRecommendedNutrientQuantityByAdviceIds(responses[1]),
                      );
                      const cropFieldIdsString = JSON.stringify(cropFieldIds);
                      this.navigated$.next();
                      this.router.navigate(['table-report', cropFieldIdsString]);
                    }),
                    finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
                    startWith(CoreActions.setLoading({ loading: true })),
                  )
                  .subscribe();
              }
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  openOrganicMatterReport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OverviewActionTypes.OpenOrganicMatterReport),
        withLatestFrom(
          this.store.pipe(takeUntil(this.destroyed$), select(selectAvailableCropFieldAdvices())),
          this.store.pipe(select(selectCrops)),
        ),
        delay(100),
        switchMap(([_, cropFieldAdvices, crops]) => {
          const selectCropFieldsDialogData = this.getFieldSelectDialogData(cropFieldAdvices, crops);
          return this.dialog.form(SelectCropFieldsDialogComponent, selectCropFieldsDialogData).pipe(
            delay(100),
            map(({ status, result }) => {
              if (status === DIALOG_RETURN_TYPES.confirm && !!result?.length) {
                const cropFieldIds = result.map((cropFieldAdvice) => cropFieldAdvice.cropFieldId);
                this.navigated$.next();
                this.router.navigate(['organic-matter-report', JSON.stringify(cropFieldIds)]);
              }
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  openNutrientBalanceReport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OverviewActionTypes.OpenNutrientBalanceReport),
        withLatestFrom(
          this.store.pipe(takeUntil(this.destroyed$), select(selectAvailableCropFieldAdvices())),
          this.store.pipe(select(selectCrops)),
        ),
        delay(100),
        switchMap(([_, cropFieldAdvices, crops]) => {
          const selectCropFieldsDialogData = this.getFieldSelectDialogData(cropFieldAdvices, crops);
          return this.dialog.form(SelectCropFieldsDialogComponent, selectCropFieldsDialogData).pipe(
            delay(100),
            map(({ status, result }) => {
              if (status === DIALOG_RETURN_TYPES.confirm && !!result?.length) {
                const cropFieldIds = result.map((cropFieldAdvice) => cropFieldAdvice.cropFieldId);
                this.navigated$.next();
                this.router.navigate(['nutrient-balance-report', JSON.stringify(cropFieldIds)]);
              }
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  openEnvironmentalImpactReport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OverviewActionTypes.OpenEnvironmentalImpactReport),
        withLatestFrom(
          this.store.pipe(takeUntil(this.destroyed$), select(selectAvailableCropFieldAdvices())),
          this.store.pipe(select(selectCrops)),
        ),
        delay(100),
        switchMap(([_, cropFieldAdvices, crops]) => {
          const selectCropFieldsDialogData = this.getFieldSelectDialogData(cropFieldAdvices, crops);
          return this.dialog.form(SelectCropFieldsDialogComponent, selectCropFieldsDialogData).pipe(
            delay(100),
            map(({ status, result }) => {
              if (status === DIALOG_RETURN_TYPES.confirm && !!result?.length) {
                const cropFieldIds = result.map((cropFieldAdvice) => cropFieldAdvice.cropFieldId);
                this.navigated$.next();
                this.router.navigate(['environmental-impact-report', JSON.stringify(cropFieldIds)]);
              }
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  openPlanetProofReport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OverviewActionTypes.OpenPlanetProofReport),
        withLatestFrom(
          this.store.pipe(takeUntil(this.destroyed$), select(selectAvailableCropFieldAdvices())),
          this.store.pipe(select(selectCrops)),
        ),
        delay(100),
        switchMap(([_, cropFieldAdvices, crops]) => {
          const selectCropFieldsDialogData = this.getFieldSelectDialogData(cropFieldAdvices, crops);
          return this.dialog.form(SelectCropFieldsDialogComponent, selectCropFieldsDialogData).pipe(
            delay(100),
            map(({ status, result }) => {
              if (status === DIALOG_RETURN_TYPES.confirm && result.length > 0) {
                const cropFieldIds = result.map((cropFieldAdvice) => cropFieldAdvice.cropFieldId);
                this.navigated$.next();
                this.router.navigate(['planet-proof', JSON.stringify(cropFieldIds)]);
              }
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  openLegalStandards$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OverviewActionTypes.OpenLegalStandards),
        delay(100),
        map((_) => {
          this.router.navigate(['fertilization-legal-standards']);
        }),
      ),
    { dispatch: false },
  );

  openSkalReport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OverviewActionTypes.OpenSkalReport),
        delay(100),
        map((_) => {
          this.router.navigate(['skal-report']);
        }),
      ),
    { dispatch: false },
  );

  routerNavigated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      withLatestFrom(this.store.pipe(select(getCropFieldIds))),
      filter(
        ([{ payload }, _]: [RouterNavigatedAction, number[]]) =>
          payload.routerState.url.indexOf('/cropfields') === 0 || payload.routerState.url === '/',
      ),
      map(
        ([_, cropFieldIds]: [RouterNavigatedAction, number[]]) =>
          new SetSelectedCropFieldIds(cropFieldIds),
      ),
    ),
  );

  deleteMultipleCropField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OverviewActionTypes.DeleteMultipleCropFields),
      mergeMap((action: DeleteMultipleCropFields) =>
        from(action.cropFieldId).pipe(
          mergeMap((itemId) =>
            this.fieldPlanService
              .deleteCropField(itemId)
              .pipe(map((_) => new CropFieldDeleted(itemId))),
          ),
          catchError((error) => {
            const validationText = this.translationHelpers.generateLocalisedValidationText(
            error.error,
            'deleteCropFields',
            );
            this.dialog.confirm({
              title: this.locale.translate('deleteCropFields.errorTitle'),
              text: validationText,
              cancelButton: false,
            });
            return EMPTY;
          }),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  constructor() {}

  private formatDecimal(num: number) {
    return formatNumber(num, this.locale.getLocale());
  }

  private getFieldSelectDialogData(
    cropFieldAdvices: CropFieldAdvice[],
    crops: CropData[],
  ): SelectCropFieldsDialogData {
    const options: SelectCropFieldsDialogItem[] = cropFieldAdvices.map((cropFieldAdvice) => {
      const hectares = `${this.formatDecimal(cropFieldAdvice.areaInHectares)} Ha`;
      const cropName = cropFieldAdvice.crop.name;
      const cropVariety = cropFieldAdvice?.cropVariety?.name
        ? ' (' + cropFieldAdvice.cropVariety.name + '),'
        : ',';
      const totalTreatments = cropFieldAdvice.treatments.length;
      const treatmentsText = this.locale
        .translate(totalTreatments === 1 ? 'treatment' : 'treatments')
        .toLowerCase();
      return {
        name: `${cropFieldAdvice.name} (${hectares})`,
        description: `${cropName}${cropVariety} ${totalTreatments} ${treatmentsText}`,
        value: cropFieldAdvice,
      };
    });

    const data: SelectCropFieldsDialogData = {
      options,
      crops,
      selectedCropIds: [-1],
      title: this.locale.translate('selectCropFieldsTitle'),
      confirmButton: options && options.length > 0,
      selected: null,
      confirmButtonText: this.locale.translate('select'),
      cancelButtonText: this.locale.translate('cancel'),
      emptyText: '',
      pushHistoryState: false,
      fullscreen: false,
      disableSelectAll: false,
    };

    return data;
  }

  private getOptionsWithTranslations(dialogOptions: ActionsDialogItem<ReportType>[]) {
    return dialogOptions.map((dialogOption) => ({
      value: dialogOption.value,
      name: this.locale.translate(dialogOption.name),
    }));
  }
}
